@let order = order$ | async;
@let productInCartCount = (cartLineItemsTotalQuantity$ | async) ?? 0;
@let orderValidationInProgress = orderValidationInProgress$ | async;

@if (isAPageWhereToHideCartDetails() && !hideCartDetails()()) {
  <div
    class="fixed inset-0 z-40 bg-black bg-opacity-60"
    (click)="toggleCartDetails()"
  ></div>
}
<div
  class="relative z-50 flex flex-col gap-2 rounded-t-xl p-5 pt-2 transition-all duration-500 ease-in-out kiosk-xl:gap-4 kiosk-xl:pt-5"
  [style.boxShadow]="'0px 0px 15px -4px ' + (topBorderColor() | contentBuilderColor)"
  [style.backgroundColor]="
    backgroundColor() | contentBuilderColor: { type: 'palette', value: 'neutral-50' }
  "
>
  @if (isAPageWhereToHideCartDetails()) {
    <div class="left-1/2 top-0 flex items-center justify-center rounded-full p-2">
      <button
        (click)="toggleCartDetails()"
        [style.backgroundColor]="
          primaryButtonBackgroundColor()
            | contentBuilderColor: { type: 'palette', value: 'neutral-400' }
        "
        class="h-1 w-[20vw] rounded border-none p-0"
        aria-label="Ouvrir le drawer"
      ></button>
    </div>
  }
  @if (errorMessages) {
    @for (message of errorMessages; track message) {
      <p class="my-5 rounded bg-error-50 px-8 py-6 font-base text-3xl text-error-700">
        {{ message }}
      </p>
    }
  }
  <app-kiosk-currency-disclaimer />
  <!-- Summary follows the parent page text color. Product cards keep product_text_color. -->
  <div class="flex flex-row items-start justify-between text-right">
    <div class="flex flex-row items-center">
      <div class="text-xl font-bold kiosk-xl:text-4xl">
        {{ 'kiosk-footer-cart.your-order' | translate }}
      </div>
      <div class="ml-5 kiosk-xl:text-3xl">
        {{ productInCartCount }}
        {{
          productInCartCount === 1
            ? ('kiosk-footer-cart.item-singular' | translate)
            : ('kiosk-footer-cart.item-plural' | translate)
        }}
      </div>
    </div>
    <div class="flex flex-row items-start">
      <p class="mr-5 font-bold kiosk-xl:text-4xl">{{ 'kiosk-footer-cart.total' | translate }}</p>
      <div class="relative font-bold">
        <div
          [class.invisible]="!orderValidationInProgress"
          class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        >
          <p class="mx-auto h-5 w-14 animate-pulse rounded bg-neutral-400 px-4 kiosk-xl:w-28"></p>
        </div>
        <div [class.invisible]="orderValidationInProgress">
          <app-price-atom
            [format]="true"
            size="4XL"
            legalCaption="youPay"
            stackAlign="end"
            [amount]="order?.final_price && productInCartCount ? (order?.final_price ?? 0) : 0"
          ></app-price-atom>
        </div>
      </div>
    </div>
  </div>
  @if (!hideCartDetails()()) {
    <app-kiosk-horizontal-scroll
      [leftButtonTemplate]="leftButton"
      [rightButtonTemplate]="rightButton"
      [contentTemplate]="content"
      #horizontalScroll
    >
      <ng-template #leftButton>
        @let leftButtonData = horizontalScroll.leftButtonData;
        <app-button-atom
          size="S"
          color="neutral"
          [icon]="'icon-arrow-down-s'"
          [iconRotation]="'180'"
          type="kiosk-light"
          thickSize="S"
          [full]="false"
          [aspectSquare]="true"
          [disabled]="leftButtonData().disabled"
        ></app-button-atom>
      </ng-template>

      <ng-template #rightButton>
        @let rightButtonData = horizontalScroll.rightButtonData;
        <app-button-atom
          size="S"
          color="neutral"
          [icon]="'icon-arrow-down-s'"
          [iconRotation]="'270'"
          type="kiosk-light"
          thickSize="S"
          [full]="false"
          [disabled]="rightButtonData().disabled"
          [aspectSquare]="true"
        ></app-button-atom>
      </ng-template>

      <ng-template #content>
        <div>
          @if (!(reconstructedShareOrderBasket$ | async)?.length) {
            @let cartByShop = reconstructedCart$ | async;
            @if (cartByShop && cartByShop.length) {
              <div>
                @for (cart of cartByShop; track $index) {
                  <div class="max-width-full flex flex-row">
                    <ul
                      class="flex flex-1 flex-row gap-8 pt-2.5"
                      #productList
                    >
                      @for (
                        product of cart[reconstructedCartKeys.Items];
                        let index = $index;
                        track $index
                      ) {
                        <li>
                          <app-kiosk-product-overview-cart-atom
                            [id]="product[reconstructedCartItemKeys.Item][cartItemKeys.Id]"
                            [name]="product[reconstructedCartItemKeys.Product][productKeys.Name]"
                            [quantity]="
                              product[reconstructedCartItemKeys.Item][cartItemKeys.Quantity]
                            "
                            [thumbnail]="
                              product[reconstructedCartItemKeys.Product].images?.thumbnail?.url
                            "
                            [dotColor]="primaryButtonBackgroundColor()"
                            [dotTextColor]="primaryButtonTextColor()"
                            [productBackgroundColor]="productBackgroundColor()"
                            [productTextColor]="productTextColor()"
                            [isAvailable]="
                              !(unAvailableProductById()?.[product.item.item_id] ?? false)
                            "
                            [hasProductToReplace]="
                              product.item.item_id
                                | appFn
                                  : mustReplaceAComposedProduct
                                  : cart[reconstructedCartKeys.Items]
                                  : unAvailableProductById()
                            "
                            (click)="onUpdateItem(product.item.id)"
                          ></app-kiosk-product-overview-cart-atom>
                        </li>
                      }
                    </ul>
                  </div>
                }
              </div>
            }
          }
        </div>
      </ng-template>
    </app-kiosk-horizontal-scroll>

    <div class="mt-2 grid grid-cols-2 gap-10 kiosk-xl:mt-8">
      <app-button-atom
        classContent="text-2xl py-2 kiosk-xl:py-10 kiosk-xl:text-4xl"
        [thickSize]="'inherit'"
        [size]="'inherit'"
        [label]="'kiosk-footer-cart.cancel' | translate"
        [overrideBgColor]="secondaryButtonBackgroundColor()"
        [overrideTextColor]="secondaryButtonTextColor()"
        type="kiosk-light"
        (click)="clearCart()"
        [full]="true"
      >
      </app-button-atom>
      <app-button-atom
        classContent="text-2xl py-2 kiosk-xl:py-10 kiosk-xl:text-4xl"
        [thickSize]="'inherit'"
        [size]="'inherit'"
        [label]="'kiosk-footer-cart.continue-to-cart' | translate"
        [overrideBgColor]="primaryButtonBackgroundColor()"
        [overrideTextColor]="primaryButtonTextColor()"
        [disabled]="!(order$ | async)?.final_price"
        type="kiosk-primary"
        (click)="navigateToCart()"
        [full]="true"
      ></app-button-atom>
    </div>
  }
</div>
